/* Created by Interactive 63 - https://interactive63.com */
/**
 * Store actions for the timeline: selecting keyframes together with their elements, dragging
 * and duplicating, deleting, reversing, distributing and managing whole tracks.
 */
import { distributeEvenly as distributeHelper } from '../../core/timeline/distribute';
import { reverseKeyframeTiming } from '../../core/timeline/reverse';
import { sampleTrack } from '../../core/timeline/sample';
import {
  findTrack,
  moveKeyframes,
  removeKeyframes,
  setKeyframe,
} from '../../core/timeline/keyframes';
import { cycleTime, MIN_PERIOD_MS } from '../../core/timeline/repeat';
import type { ElementId, Project, PropertyKey, Track, TrackRepeat } from '../../core/model/types';
import type { SelectionMode } from './actions';
import type { Store } from './store';

/** Returns the elements that own the given keyframes. */
function elementsOf(project: Project, keyframeIds: readonly string[]): ElementId[] {
  const wanted = new Set(keyframeIds);
  return [
    ...new Set(
      project.tracks
        .filter((t) => t.keyframes.some((kf) => wanted.has(kf.id)))
        .map((t) => t.elementId),
    ),
  ];
}

/** Selects keyframes and the elements they belong to, so the inspector shows the right element. */
export function selectKeyframesWithElements(
  store: Store,
  ids: readonly string[],
  mode: SelectionMode = 'replace',
): void {
  const { project } = store.getState();
  store.updateEditor((draft) => {
    const current = draft.selection.keyframeIds;
    const next =
      mode === 'replace'
        ? [...ids]
        : mode === 'add'
          ? [...new Set([...current, ...ids])]
          : [
              ...current.filter((id) => !ids.includes(id)),
              ...ids.filter((id) => !current.includes(id)),
            ];
    draft.selection.keyframeIds = next;
    if (next.length > 0) draft.selection.elementIds = elementsOf(project, next);
  });
}

/** Selects every keyframe on the given tracks. */
export function selectAllKeyframesOn(store: Store, trackIds: readonly string[]): void {
  const ids = store
    .getState()
    .project.tracks.filter((t) => trackIds.includes(t.id))
    .flatMap((t) => t.keyframes.map((kf) => kf.id));
  selectKeyframesWithElements(store, ids);
}

/** Selects every keyframe on the selected elements. */
export function selectAllKeyframesOnSelected(store: Store): void {
  const { project, editor } = store.getState();
  const ids = project.tracks
    .filter((t) => editor.selection.elementIds.includes(t.elementId))
    .flatMap((t) => t.keyframes.map((kf) => kf.id));
  store.updateEditor((draft) => {
    draft.selection.keyframeIds = ids;
  });
}

/**
 * Moves keyframes by a total delta measured from where the drag began. Every call starts again
 * from the original tracks, so passing over other keyframes never destroys them, and the whole
 * drag collapses into one undo step through the coalescing key.
 */
export function dragKeyframesFromBase(
  store: Store,
  baseTracks: readonly Track[],
  ids: readonly string[],
  totalDelta: number,
): void {
  store.updateProject(
    'Move keyframes',
    (draft) => {
      draft.tracks = structuredClone(baseTracks) as Track[];
      moveKeyframes(draft, ids, totalDelta);
    },
    { coalesceKey: 'drag:keyframes' },
  );
}

/** Copies keyframes forward by a delta, returning the new IDs. Existing keyframes at the target replace. */
export function duplicateKeyframesBy(
  store: Store,
  ids: readonly string[],
  delta: number,
): string[] {
  const wanted = new Set(ids);
  const created: string[] = [];
  store.updateProject('Duplicate keyframes', (draft) => {
    const sources = draft.tracks.flatMap((t) =>
      t.keyframes
        .filter((kf) => wanted.has(kf.id))
        .map((kf) => ({ elementId: t.elementId, property: t.property, kf: { ...kf } })),
    );
    for (const { elementId, property, kf } of sources) {
      const copy = setKeyframe(draft, elementId, property, kf.time + delta, kf.value, kf.easing);
      if (kf.motion) copy.motion = { ...kf.motion };
      created.push(copy.id);
    }
  });
  store.updateEditor((draft) => {
    draft.selection.keyframeIds = created;
  });
  return created;
}

/** Deletes keyframes and clears them from the selection. */
export function deleteKeyframes(store: Store, ids: readonly string[]): void {
  if (ids.length === 0) return;
  store.transaction('Delete keyframes', () => {
    store.updateProject('Delete keyframes', (draft) => {
      removeKeyframes(draft, ids);
    });
    store.updateEditor((draft) => {
      draft.selection.keyframeIds = draft.selection.keyframeIds.filter((id) => !ids.includes(id));
    });
  });
}

/** Reverses the timing of the given keyframes. */
export function reverseTiming(store: Store, ids: readonly string[]): void {
  store.updateProject('Reverse timing', (draft) => {
    reverseKeyframeTiming(draft, ids);
  });
}

/** Spaces the given keyframes evenly on each track. */
export function distributeKeyframes(store: Store, ids: readonly string[]): void {
  store.updateProject('Distribute keyframes', (draft) => {
    distributeHelper(draft, ids);
  });
}

/** Enables or disables tracks. */
export function setTracksEnabled(
  store: Store,
  trackIds: readonly string[],
  enabled: boolean,
): void {
  store.updateProject(enabled ? 'Enable track' : 'Disable track', (draft) => {
    for (const track of draft.tracks) if (trackIds.includes(track.id)) track.enabled = enabled;
  });
}

/** Removes tracks and the keyframe selection that pointed into them. */
export function removeTracks(store: Store, trackIds: readonly string[]): void {
  store.updateProject('Remove track', (draft) => {
    draft.tracks = draft.tracks.filter((t) => !trackIds.includes(t.id));
  });
}

/** Removes every track of the given elements. */
export function removeElementAnimation(store: Store, elementIds: readonly ElementId[]): void {
  store.updateProject('Remove animation', (draft) => {
    draft.tracks = draft.tracks.filter((t) => !elementIds.includes(t.elementId));
  });
}

/** Adds a keyframe on a track at a time, holding the track's sampled value there. */
export function addKeyframeAtTime(
  store: Store,
  elementId: ElementId,
  property: PropertyKey,
  time: number,
): string | undefined {
  const track = findTrack(store.getState().project, elementId, property);
  if (!track) return undefined;
  const value = sampleTrack(track, time, store.getState().project.scene.duration);
  if (value === undefined) return undefined;
  // A repeating track is edited in its first cycle, wherever in the scene the click landed.
  const local = track.repeat ? Math.round(cycleTime(track, time).time) : time;
  let id: string | undefined;
  store.updateProject('Add keyframe', (draft) => {
    id = setKeyframe(draft, elementId, property, local, value).id;
  });
  return id;
}

/** Sets the timeline zoom (pixels per second) and horizontal scroll. */
export function setTimelineView(store: Store, zoom: number, scroll: number): void {
  store.updateEditor((draft) => {
    draft.timelineZoom = zoom;
    draft.timelineScroll = Math.max(0, scroll);
  });
}

export interface TrackPlaybackPatch {
  /** A repeat to apply, or null to remove it. Leave undefined to keep the current one. */
  repeat?: TrackRepeat | null;
  /** false = play during the first scene iteration only; true = restart with the scene. */
  restartWithScene?: boolean;
}

/** Changes how tracks repeat and whether they restart when the scene loops. */
export function setTrackPlayback(
  store: Store,
  trackIds: readonly string[],
  patch: TrackPlaybackPatch,
): void {
  store.updateProject('Change track playback', (draft) => {
    for (const track of draft.tracks) {
      if (!trackIds.includes(track.id)) continue;
      if (patch.repeat === null) {
        delete track.repeat;
      } else if (patch.repeat) {
        const last = track.keyframes[track.keyframes.length - 1]?.time ?? 0;
        // An explicit cycle can never be shorter than the keyframes it holds.
        const period =
          patch.repeat.period === null ? null : Math.max(patch.repeat.period, last, MIN_PERIOD_MS);
        track.repeat = { ...patch.repeat, period };
      }
      if (patch.restartWithScene === false) track.restartWithScene = false;
      else if (patch.restartWithScene === true) delete track.restartWithScene;
    }
  });
}
